import { useState } from "react";

import { type UserImageMedia, UserImageType } from "@/schemas/user/image";
import ProfileService, {
  UserProfileImageUpdateType,
} from "@/services/user/profile";
import { type AppUserSession } from "@/schemas/auth/session";
import { useSession } from "@/context/auth/useSession";
import universalUploadImage from "@/utils/image/universalUploadImage";

type UseAvatarUploadResult = {
  isLoading: boolean;
  pickAndUploadImageAsAvatar: () => Promise<void>;
};

export const useAvatarUpload = (
  session: AppUserSession,
): UseAvatarUploadResult => {
  const [isUploading, setIsUploading] = useState(false);
  const { setIdentity } = useSession();

  const uploadLocalUserIdentityAvatar = (image: UserImageMedia): void => {
    // Update avatar image in UserIdentity locally
    const updatedLocalIdentity = {
      ...session.identity,
      avatar: image,
    };
    setIdentity(updatedLocalIdentity);
  };

  const updateProfileAvatar = async (image: UserImageMedia): Promise<void> => {
    const profileService = new ProfileService(session);
    await profileService.updateProfile(
      UserProfileImageUpdateType.AVATAR,
      image,
    );
  };

  const pickAndUploadImageAsAvatar = async (): Promise<void> => {
    await universalUploadImage({
      session,
      imageType: UserImageType.AVATAR,
      // allowMultiple = false, // this prop is not implemented yet
      aspectRatio: { width: 1, height: 1 },
      compressionQuality: 0.6, // TODO: use ImageManipulator to reduce size
      onUploadStart: () => setIsUploading(true),
      onUploadSuccess: async (userImages) => {
        if (userImages && userImages.length > 0 && userImages[0]) {
          const avatarImage = userImages[0];
          await updateProfileAvatar(avatarImage);
          uploadLocalUserIdentityAvatar(avatarImage);
        }
      },
      onUploadEnd: () => setIsUploading(false),
    });
  };

  return {
    isLoading: isUploading,
    pickAndUploadImageAsAvatar,
  };
};
